/**
 * Sticky sheet header: title, close, "N of M confirmed" bar, chips and Save.
 * `sof_review_summary` keeps the legacy sentence so the e2e count assertions
 * (total · confirmed · AI-suggested · unrated order) still hold.
 */
import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

import { reviewDesk } from '@/design-system';
import { useTranslation } from '@/hooks/useTranslation';
import type { EngagementLevel, EngagementSessionRow, SessionObservationForm } from '@/types/mentorEngagements';
import { ActionButton, Chip, mentorStyles } from '../shared';
import type { sofReviewSummary } from '../sofReview';

interface Props {
  session: EngagementSessionRow;
  form: SessionObservationForm;
  level: EngagementLevel;
  summary: ReturnType<typeof sofReviewSummary>;
  phone: boolean;
  locked: boolean;
  saving: boolean;
  dirty: boolean;
  error: boolean;
  onClose: () => void;
  onSave: () => void;
}

export function SofProgressHeader({ session, form, level, summary, phone, locked, saving, dirty, error, onClose, onSave }: Props) {
  const { t } = useTranslation();
  const confirmed = summary.mentorConfirmed;
  const ratio = summary.total ? confirmed / summary.total : 0;
  const title = t('mentorEngagements.review.title', { number: session.session_number, date: session.session_date || '—' });
  return (
    <View style={[styles.header, phone && styles.headerPhone]}>
      <View style={styles.titleRow}>
        <View style={{ flex: 1, minWidth: 0, gap: 6 }}>
          <Text style={[mentorStyles.panelTitle, phone && { fontSize: 16 }]} numberOfLines={1}>{title}</Text>
          <View style={mentorStyles.row}>
            <Chip label={level.toUpperCase()} tone="accent" />
            <Chip label={t(`mentorEngagements.review.${session.source === 'mcai' ? 'uploaded' : 'manual'}`)} icon={session.source === 'mcai' ? 'graphic-eq' : 'edit-note'} />
            <Chip label={t(`mentorEngagements.review.${form.status}`)} tone={form.status === 'final' ? 'success' : 'neutral'} icon={form.status === 'final' ? 'verified' : undefined} />
          </View>
        </View>
        {!phone && !locked ? <ActionButton testID="sof_review_save" label={t('mentorEngagements.review.save')} variant={dirty ? 'primary' : 'secondary'} icon="save" disabled={saving} onPress={onSave} /> : null}
        <Pressable
          onPress={onClose}
          disabled={saving}
          accessibilityState={{ disabled: saving }}
          accessibilityRole="button"
          accessibilityLabel={t('mentorEngagements.common.close', { title })}
          style={({ pressed }) => [{ padding: 4 }, pressed && mentorStyles.pressed]}
        >
          <MaterialIcons name="close" size={22} color={reviewDesk.inkSoft} />
        </Pressable>
      </View>
      <View testID="sof_review_progress" style={styles.progressRow}>
        <View style={styles.track}><View style={[styles.fill, { width: `${Math.round(ratio * 100)}%` }, ratio === 1 && { backgroundColor: reviewDesk.success }]} /></View>
        <Text style={styles.progressText}>{t('mentorEngagements.review.progress', { confirmed, total: summary.total })}</Text>
      </View>
      <Text testID="sof_review_summary" accessibilityLiveRegion="polite" style={styles.summary}>{t('mentorEngagements.review.summary', summary)}</Text>
      {form.status === 'final' && form.mentor_printed_name ? <Text style={mentorStyles.mutedText}>{t('mentorEngagements.review.signed', { name: form.mentor_printed_name, date: form.signed_at?.slice(0, 10) || '—' })}</Text> : null}
      {dirty ? <Text style={styles.unsaved}>{t('mentorEngagements.review.unsaved')}</Text> : null}
      {error ? <Text accessibilityRole="alert" style={mentorStyles.errorText}>{t('mentorEngagements.review.saveError')}</Text> : null}
    </View>
  );
}

const styles = StyleSheet.create({
  header: { gap: 10, paddingHorizontal: 20, paddingTop: 16, paddingBottom: 12, borderBottomWidth: 1, borderBottomColor: reviewDesk.line, backgroundColor: reviewDesk.card },
  headerPhone: { paddingHorizontal: 16, paddingTop: 12 },
  titleRow: { flexDirection: 'row', alignItems: 'flex-start', gap: 12 },
  progressRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  track: { flex: 1, height: 8, borderRadius: 4, backgroundColor: reviewDesk.canvasMuted, overflow: 'hidden' },
  fill: { height: '100%', borderRadius: 4, backgroundColor: reviewDesk.accent },
  progressText: { color: reviewDesk.ink, fontSize: 13, fontWeight: '800', fontVariant: ['tabular-nums'] },
  summary: { color: reviewDesk.muted, fontSize: 12, lineHeight: 17 },
  unsaved: { color: '#8A5B00', fontSize: 12, fontWeight: '600' },
});
